import React from 'react';
import { cn } from '@rocco/utils/cn';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigBarItemColor } from '@rocco/ui/filter-sort/types/filter-config-bar';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { FilterBarElementProps } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBarElement';

/**
 * Validates a color string to prevent CSS injection attacks.
 * Allows hex colors and linear-gradient values with hex colors.
 */
const isValidColorValue = (value: string): boolean => {
    const trimmed = value.trim();
    // Match hex colors: #RGB, #RRGGBB, #RRGGBBAA
    const hexPattern = /^#[0-9a-fA-F]{3,8}$/;
    // Match linear-gradient with hex colors and percentages (safe subset)
    const gradientPattern =
        /^linear-gradient\s*\(\s*\d+deg\s*,\s*(#[0-9a-fA-F]{3,8}\s+\d+%\s*,?\s*)+\)$/;

    return hexPattern.test(trimmed) || gradientPattern.test(trimmed);
};

/**
 * Parses and validates color value, returning a safe background CSS value.
 * Returns a fallback color if validation fails.
 */
const getSafeBackground = (label: string | null | undefined): string => {
    if (!label) return '#fff';

    // If label starts with #, it might be comma-separated hex colors
    if (label.startsWith('#')) {
        const colors = label.split(',').filter(isValidColorValue);
        if (colors.length === 0) return '#fff';

        const firstColor = colors[0];
        if (!firstColor) return '#fff';

        return colors.length > 1
            ? `linear-gradient(to right, ${colors.join(', ')})`
            : firstColor;
    }

    // If label is a linear-gradient, validate it
    if (label.startsWith('linear-gradient') && isValidColorValue(label)) {
        return label;
    }

    // Fallback for invalid values
    return '#fff';
};

/**
 * Color swatch filter for selecting colors from a grid.
 * Displays circular color swatches with selection ring and validates color values for security.
 *
 * @example
 * <FiltersBarColors filterConfigItem={colorConfig} filterState={colorState} onFilterStateUpdate={updateFilter} />
 */
export const FiltersBarColors = (
    props: FilterBarElementProps<
        FilterConfigBarItemColor,
        MultipleValuesFilterState
    >,
) => {
    const { filterState, onFilterStateUpdate } = props;
    const {
        searchFilters: { handleToggleMultipleValues },
    } = useFiltersContext();

    return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {filterState.items.map((item) => {
                // Get validated background color
                // item.label can be:
                // - merged hex colors like "#fff,#aaa,#000" (shown as linear gradient)
                // - raw CSS value like "linear-gradient(180deg, #D8D8D8 0%, #B4B4B4 100%)"
                const background = getSafeBackground(item.label);

                return (
                    <button
                        key={item.value}
                        type="button"
                        aria-label={`Select color ${item.value}`}
                        aria-pressed={item.checked}
                        className={cn(
                            'flex cursor-pointer items-center gap-2 text-left transition-all',
                            'focus:outline-hidden rounded focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1',
                            item.checked ? 'font-medium' : '',
                        )}
                        data-color={item.value}
                        onClick={() => {
                            if (handleToggleMultipleValues) {
                                onFilterStateUpdate(
                                    handleToggleMultipleValues(
                                        filterState,
                                        item,
                                        !item.checked,
                                    ),
                                    true, // refetch = true to update URL
                                );
                            }
                        }}
                    >
                        <span
                            className={cn(
                                'size-5 shrink-0 rounded-full border-2',
                                item.checked
                                    ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1'
                                    : 'border-gray-200',
                            )}
                            style={{
                                background,
                                backgroundOrigin: 'border-box',
                            }}
                        />
                        <span className="truncate text-3.5 capitalize">
                            {item.value}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
